import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, ChevronDown, ChevronUp, GraduationCap } from 'lucide-react';
import { programsData, programCategories } from '../data/programsData';
import { SEO, pageSEO } from '../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export default function Programs() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPrograms = programsData.filter(p => {
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.shortName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleProgram = (programId) => {
    setExpandedProgram(expandedProgram === programId ? null : programId);
  };

  return (
    <div data-testid="programs-page" className="min-h-screen pt-20">
      <SEO {...pageSEO.programs} url="/programs" />
      {/* Hero Section - ALU Style */}
      <section className="relative py-24 bg-[#0D4A2E] pattern-overlay">
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
            Degree <span className="text-[#C5A028]">Programs</span>
          </h1>
          <p className="text-white/70 text-xl max-w-2xl">
            {programsData.length} accredited programs across Health Sciences, Education, 
            Agriculture, and Administration. Choose your track: 2, 3, or 4 years.
          </p>
        </div>
      </section>

      {/* Filters - ALU Style */}
      <section className="py-8 bg-[#F5F5F5] border-b border-[#E5E5E5] sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Search */}
            <div className="relative max-w-md flex-grow">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666666]" />
              <input
                type="text"
                placeholder="Search programs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-[#E5E5E5] bg-white focus:border-[#0D4A2E] focus:outline-none transition-colors duration-200"
              />
            </div>
            
            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                data-testid="filter-all"
                className={`px-5 py-2 text-sm font-medium transition-all duration-200 ${
                  selectedCategory === 'all'
                    ? 'bg-[#0D4A2E] text-white'
                    : 'bg-white text-[#666666] hover:bg-[#0D4A2E] hover:text-white border border-[#E5E5E5]'
                }`}
              >
                All ({programsData.length})
              </button>
              {programCategories.map((category) => {
                const count = programsData.filter(p => p.category === category).length;
                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`px-5 py-2 text-sm font-medium transition-all duration-200 ${
                      selectedCategory === category
                        ? 'bg-[#0D4A2E] text-white'
                        : 'bg-white text-[#666666] hover:bg-[#0D4A2E] hover:text-white border border-[#E5E5E5]'
                    }`}
                  >
                    {category} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Programs List */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          {filteredPrograms.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#666666] text-lg">No programs found. Try adjusting your search.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredPrograms.map((program) => (
                <div
                  key={program.id}
                  data-testid={`program-${program.id}`}
                  className={`border transition-all duration-300 ${
                    expandedProgram === program.id 
                      ? 'border-[#0D4A2E] shadow-lg' 
                      : 'border-[#E5E5E5] hover:border-[#0D4A2E]/50'
                  }`}
                >
                  {/* Program Header */}
                  <div 
                    className="p-6 cursor-pointer flex items-center gap-6"
                    onClick={() => toggleProgram(program.id)}
                  >
                    <div className="w-16 h-16 bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="w-8 h-8 text-[#0D4A2E]" />
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-3 py-1 bg-[#C5A028] text-[#1A1A1A] text-xs font-bold">
                          {program.shortName}
                        </span>
                        <span className="text-[#666666] text-sm">{program.category}</span>
                      </div>
                      <h3 className="text-xl font-bold text-[#0D4A2E]">{program.name}</h3>
                      <p className="text-[#666666] text-sm mt-1">{program.description}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <a 
                        href="https://cnte.ciu.edu.lr/apply"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="hidden md:inline-flex px-6 py-3 bg-[#0D4A2E] text-white text-sm font-semibold hover:bg-[#082D1C] transition-colors duration-200"
                      >
                        Apply Now
                      </a>
                      <button className="p-2">
                        {expandedProgram === program.id ? (
                          <ChevronUp className="w-6 h-6 text-[#0D4A2E]" />
                        ) : (
                          <ChevronDown className="w-6 h-6 text-[#666666]" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedProgram === program.id && (
                    <div className="border-t border-[#E5E5E5] bg-[#F5F5F5] p-6">
                      <h4 className="font-bold text-[#0D4A2E] mb-4">Curriculum - 8 Semesters</h4>
                      <p className="text-[#666666] text-sm mb-6">
                        All tracks (Traditional, Standard, Fast Track) complete the same curriculum.
                      </p>
                      
                      <Accordion type="single" collapsible className="space-y-2">
                        {program.semesters.map((semester, index) => (
                          <AccordionItem 
                            key={index} 
                            value={`semester-${index}`}
                            className="bg-white border border-[#E5E5E5]"
                          >
                            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-[#F5F5F5]">
                              <div className="flex items-center gap-4 text-left">
                                <span className="w-8 h-8 bg-[#0D4A2E] text-white flex items-center justify-center text-sm font-bold">
                                  {index + 1}
                                </span>
                                <div>
                                  <div className="font-semibold text-[#0D4A2E]">{semester.name}</div>
                                  <div className="text-sm text-[#666666]">{semester.title}</div>
                                </div>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                                {semester.courses.map((course, courseIndex) => (
                                  <div 
                                    key={courseIndex}
                                    className="flex items-center gap-3 p-3 bg-[#F5F5F5] border-l-2 border-[#C5A028]"
                                  >
                                    <div className="flex-grow">
                                      <div className="text-sm font-medium text-[#0D4A2E]">{course.name}</div>
                                      <div className="text-xs text-[#666666]">
                                        {course.code} • {course.credits > 0 ? `${course.credits} Credits` : 'Practical'}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>

                      <div className="mt-6 flex gap-4">
                        <a 
                          href="https://cnte.ciu.edu.lr/apply"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-6 py-3 font-semibold hover:bg-[#D4B43D] transition-colors duration-200"
                        >
                          <ArrowRight className="w-4 h-4" />
                          Apply for {program.shortName}
                        </a>
                        <Link 
                          to="/contact"
                          className="inline-flex items-center gap-2 border-2 border-[#0D4A2E] text-[#0D4A2E] px-6 py-3 font-semibold hover:bg-[#0D4A2E] hover:text-white transition-colors duration-200"
                        >
                          Request Info
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Track Options - ALU Style */}
      <section className="py-20 bg-[#0D4A2E] text-white pattern-overlay">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-center">
            Choose Your <span className="text-[#C5A028]">Track</span>
          </h2>
          <p className="text-white/70 text-center mb-12 max-w-2xl mx-auto">
            All tracks complete the same 8-semester curriculum. Choose the schedule that fits your lifestyle.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {[
              { years: '4', title: 'Traditional Track', schedule: '2 semesters/year', desc: 'Standard pace for full-time students or those with busy schedules.' },
              { years: '3', title: 'Standard Track', schedule: '~3 semesters/year', desc: 'Accelerated pace. Great for working professionals seeking advancement.' },
              { years: '2', title: 'Fast Track', schedule: '4 semesters/year', desc: 'Intensive pace. For dedicated students ready to fast-track their career.' },
            ].map((track, index) => (
              <div 
                key={index}
                className="bg-white/5 p-8 text-center border border-white/10 hover:bg-white/10 transition-colors duration-300"
              >
                <div className="w-20 h-20 rounded-full bg-[#C5A028] flex items-center justify-center mx-auto mb-6">
                  <span className="font-bold text-3xl text-[#1A1A1A]">{track.years}</span>
                </div>
                <h3 className="text-xl font-bold mb-2">{track.title}</h3>
                <p className="text-[#C5A028] text-sm font-medium mb-4">{track.schedule}</p>
                <p className="text-white/60 text-sm">{track.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#0D4A2E] mb-4">Ready to Start?</h2>
          <p className="text-[#666666] mb-8">
            Take the first step towards advancing your career with CEPRES International University.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://cnte.ciu.edu.lr/apply"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold hover:bg-[#D4B43D] transition-colors duration-200"
            >
              <ArrowRight className="w-5 h-5" />
              APPLY NOW
            </a>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 border-2 border-[#0D4A2E] text-[#0D4A2E] px-8 py-4 font-bold hover:bg-[#0D4A2E] hover:text-white transition-colors duration-200"
            >
              CONTACT US
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
